import { useMemo, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { useNavigate } from 'react-router-dom';

import { useNotifications } from 'hooks/useNotifications';
import type { Notification } from 'api/services/notificationService';
import { fmtFechaHora } from 'utils/formatDate';

// ==============================|| CAMPANITA ||============================== //
//
// Notificaciones reales del usuario (db/041), en dos pestañas:
//
//   Nuevas  las que no ha leído
//   Leídas  el historial
//
// Al dar clic a una se marca como leída —con eso pasa sola a la otra
// pestaña— y se abre en un modal central. El modal existe porque en la lista
// el texto va recortado y varias notificaciones traen el nombre completo de
// la obra y el nombre de carpeta, que no se alcanzan a leer en 340 px.

const fechaLarga = (iso: string) => fmtFechaHora(iso);

const hace = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  return d === 1 ? 'ayer' : `hace ${d} días`;
};

export default function NotificationsBell() {
  const navigate = useNavigate();
  const { items, unread, loading, load, marcarLeida, marcarTodas } = useNotifications();

  const [pestana, setPestana] = useState<'nuevas' | 'leidas'>('nuevas');
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [abierta, setAbierta] = useState<Notification | null>(null);

  const nuevas = useMemo(() => items.filter((n) => !n.read), [items]);
  const leidas = useMemo(() => items.filter((n) => n.read), [items]);
  const lista = pestana === 'nuevas' ? nuevas : leidas;

  const abrir = (n: Notification) => {
    // Al abrirla se da por leída: pasa sola a la pestaña de Leídas.
    if (!n.read) marcarLeida(n.keyNotification);
    setMenuAbierto(false);   // el menú se cierra y queda el modal
    setAbierta({ ...n, read: true });
  };

  const irALaPantalla = () => {
    const destino = abierta?.url;
    setAbierta(null);
    if (destino) navigate(destino);
  };

  return (
    <>
      {/* autoClose="outside": sin esto, cualquier clic DENTRO cierra el menú
          y cambiar de pestaña sería imposible. Al abrir una notificación se
          cierra a mano. */}
      <Dropdown
        className="pc-h-item"
        align="end"
        show={menuAbierto}
        autoClose="outside"
        onToggle={(ab) => { setMenuAbierto(ab); if (ab) load(); }}
      >
        <Dropdown.Toggle className="pc-head-link me-0 arrow-none position-relative" variant="link" id="notifications-dropdown">
          <i className="ph ph-bell" />
          {unread > 0 && (
            // El contador va FUERA de la campana, no encimado: antes quedaba
            // pegado al ícono con letra de .6rem y se leía como una mancha.
            <span
              className="position-absolute d-inline-flex align-items-center justify-content-center fw-bold"
              style={{
                top: -3, right: -5, minWidth: 19, height: 19, padding: '0 5px',
                fontSize: 11, lineHeight: 1, borderRadius: 10,
                background: '#e03030', color: '#fff',
                border: '2px solid var(--ga-s1, #0c1520)',
                boxShadow: '0 0 6px rgba(224,48,48,.55)',
              }}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu className="pc-h-dropdown p-0" style={{ width: 360, maxWidth: '94vw' }}>
          <div className="d-flex justify-content-between align-items-center px-3 pt-2">
            <h6 className="mb-0">Notificaciones</h6>
            {unread > 0 && (
              <button className="btn btn-sm btn-link p-0 text-decoration-none" onClick={marcarTodas}>
                Marcar todas
              </button>
            )}
          </div>

          {/* Dos pestañas: lo pendiente y el historial */}
          <ul className="nav nav-tabs px-2 pt-2">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link py-1 ${pestana === 'nuevas' ? 'active' : ''}`}
                onClick={() => setPestana('nuevas')}
              >
                Nuevas
                {nuevas.length > 0 && <span className="badge text-bg-danger ms-2">{nuevas.length}</span>}
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link py-1 ${pestana === 'leidas' ? 'active' : ''}`}
                onClick={() => setPestana('leidas')}
              >
                Leídas
                {leidas.length > 0 && <span className="badge text-bg-secondary ms-2">{leidas.length}</span>}
              </button>
            </li>
          </ul>

          <div style={{ maxHeight: '58vh', overflowY: 'auto' }}>
            {loading && items.length === 0 && (
              <div className="text-center text-muted py-4 small">
                <span className="spinner-border spinner-border-sm me-2" />Cargando…
              </div>
            )}

            {!loading && lista.length === 0 && (
              <div className="text-center text-muted py-4 small">
                <i className={`ph ${pestana === 'nuevas' ? 'ph-check-circle' : 'ph-tray'} d-block mb-1`} style={{ fontSize: 28 }} />
                {pestana === 'nuevas' ? 'No tienes notificaciones nuevas.' : 'Todavía no has leído ninguna.'}
              </div>
            )}

            {lista.map((n) => (
              <button
                key={n.keyNotification}
                type="button"
                className="btn text-start w-100 px-3 py-2 border-0 border-bottom rounded-0 d-flex gap-2"
                onClick={() => abrir(n)}
                style={{ background: n.read ? 'transparent' : 'rgba(30,127,212,.08)' }}
              >
                <i className={`ph ${n.icon ?? 'ph-info'} mt-1`} style={{ fontSize: 18, opacity: 0.8 }} />
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="small fw-semibold text-truncate">{n.title}</div>
                  {n.message && (
                    <div className="text-muted text-truncate" style={{ fontSize: '.78rem' }}>{n.message}</div>
                  )}
                  <div className="text-muted" style={{ fontSize: '.7rem' }}>{hace(n.dateCreate)}</div>
                </div>
                {!n.read && (
                  <span className="rounded-circle bg-primary align-self-center flex-shrink-0" style={{ width: 8, height: 8 }} />
                )}
              </button>
            ))}
          </div>
        </Dropdown.Menu>
      </Dropdown>

      {/* Modal central: el texto completo, sin recortes */}
      <Modal show={!!abierta} onHide={() => setAbierta(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 d-flex align-items-center gap-2">
            <i className={`ph ${abierta?.icon ?? 'ph-info'}`} style={{ fontSize: 20 }} />
            Notificación
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <h6 style={{ overflowWrap: 'anywhere' }}>{abierta?.title}</h6>

          {abierta?.message && (
            <p className="mb-3" style={{ overflowWrap: 'anywhere' }}>{abierta.message}</p>
          )}

          <div className="small text-muted">
            <div><i className="ph ph-clock me-1" />{abierta && fechaLarga(abierta.dateCreate)}</div>
            {abierta?.actorName && <div><i className="ph ph-user me-1" />{abierta.actorName}</div>}
          </div>
        </Modal.Body>

        <Modal.Footer className="d-grid gap-2 d-sm-flex">
          <Button variant="outline-secondary" onClick={() => setAbierta(null)}>Cerrar</Button>
          {abierta?.url && (
            <Button variant="primary" onClick={irALaPantalla}>
              <i className="ph ph-arrow-square-out me-1" />Ir a la pantalla
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
